'use client';

/**
 * Rx support desk — patient panel next to a conversation.
 *
 * Who the patient is, their treatments on this brand, the order behind each
 * one, the last tracking and the next renewal, with the actions support
 * takes most: refund, stop renewals, send the tracking again. Presentational:
 * the container owns the tRPC hooks.
 */

import * as React from 'react';
import { format } from 'date-fns';
import { Ban, Loader2, PackageCheck, Receipt, RotateCcw, Truck, UserRound } from 'lucide-react';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@tagadapay/trpc-types';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

type AdminOutputs = inferRouterOutputs<AppRouter>['rxSupport']['admin'];
export type DeskContext = AdminOutputs['context'];
type DeskCase = DeskContext['cases'][number];

export type PatientPanelActions = {
  onRefund: (input: { caseId: string; amountMinor?: number; reason: string }) => Promise<void>;
  onCancelRenewal: (input: { caseId: string; reason: string }) => Promise<void>;
  onResendTracking: (input: { caseId: string }) => Promise<void>;
};

const CASE_STATUS: Record<string, { label: string; tone: string }> = {
  requested: { label: 'Requested', tone: 'bg-zinc-100 text-zinc-700' },
  submitted: { label: 'With clinician', tone: 'bg-sky-50 text-sky-800' },
  in_review: { label: 'In review', tone: 'bg-sky-50 text-sky-800' },
  approved: { label: 'Approved', tone: 'bg-emerald-50 text-emerald-800' },
  shipped: { label: 'Shipped', tone: 'bg-emerald-50 text-emerald-800' },
  delivered: { label: 'Delivered', tone: 'bg-emerald-50 text-emerald-800' },
  declined: { label: 'Declined', tone: 'bg-red-50 text-red-800' },
  cancelled: { label: 'Cancelled', tone: 'bg-zinc-100 text-zinc-700' },
  error: { label: 'Error', tone: 'bg-red-50 text-red-800' },
};

const money = (minor: number | null | undefined, currency: string | null | undefined) =>
  ((minor ?? 0) / 100).toLocaleString('en-US', { style: 'currency', currency: (currency ?? 'USD').toUpperCase() });

const day = (d: Date | string | null | undefined) => (d ? format(new Date(d), 'MMM d, yyyy') : '—');

/** Public tracking page for the common US carriers; the raw number otherwise. */
const trackingUrl = (carrier: string | null, number: string): string | null => {
  const c = (carrier ?? '').toLowerCase();
  if (c.includes('ups')) return `https://www.ups.com/track?tracknum=${encodeURIComponent(number)}`;
  if (c.includes('fedex')) return `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(number)}`;
  if (c.includes('usps')) return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(number)}`;
  if (c.includes('dhl')) return `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${encodeURIComponent(number)}`;
  return null;
};

type DialogState =
  | { kind: 'refund'; c: DeskCase }
  | { kind: 'renewal'; c: DeskCase }
  | { kind: 'tracking'; c: DeskCase }
  | null;

export function RxSupportPatientPanel({
  context,
  loading,
  actions,
}: {
  context: DeskContext | undefined;
  loading: boolean;
  actions?: PatientPanelActions;
}) {
  const [dialog, setDialog] = React.useState<DialogState>(null);

  if (loading && !context) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!context) return null;

  const { customer, cases } = context;
  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-start gap-2">
        <UserRound className="text-muted-foreground mt-0.5 h-4 w-4" />
        <div className="min-w-0 text-sm">
          {customer ? (
            <>
              <div className="font-medium">
                {[customer.firstName, customer.lastName].filter(Boolean).join(' ') || 'Patient'}
              </div>
              <div className="text-muted-foreground truncate font-mono text-xs">{customer.email}</div>
            </>
          ) : (
            <div className="text-muted-foreground">No patient account matches this email on this brand.</div>
          )}
        </div>
      </div>

      {cases.length === 0 ? (
        <p className="text-muted-foreground text-sm">No treatment found for this patient.</p>
      ) : (
        <ul className="space-y-3">
          {cases.map((c) => (
            <CaseCard key={c.id} c={c} actions={actions} onAction={setDialog} />
          ))}
        </ul>
      )}

      {actions && dialog ? (
        <ActionDialog state={dialog} actions={actions} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}

function CaseCard({
  c,
  actions,
  onAction,
}: {
  c: DeskCase;
  actions?: PatientPanelActions;
  onAction: (s: DialogState) => void;
}) {
  const status = CASE_STATUS[c.status] ?? { label: c.status, tone: 'bg-zinc-100 text-zinc-700' };
  const order = c.order;
  const refundable = order ? Math.max(0, (order.paidAmount ?? 0) - (order.refundedAmount ?? 0)) : 0;
  const canRefund = Boolean(order) && order?.status !== 'refunded';
  const canStopRenewal =
    !c.renewalCancelled && !['declined', 'cancelled', 'error'].includes(c.status);
  const url = c.tracking ? trackingUrl(c.tracking.carrier, c.tracking.trackingNumber) : null;

  return (
    <li className={cn('rounded-lg border p-3 text-sm', c.isRequestCase && 'border-primary/40 bg-primary/5')}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-medium">{c.productName ?? 'Treatment'}</div>
          <div className="text-muted-foreground text-xs">
            Started {day(c.createdAt)}
            {c.parentCaseId ? ' · renewal' : ''}
            {c.isRequestCase ? ' · this request' : ''}
          </div>
        </div>
        <span className={cn('shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium', status.tone)}>{status.label}</span>
      </div>

      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        <dt className="text-muted-foreground flex items-center gap-1">
          <Receipt className="h-3 w-3" /> Order
        </dt>
        <dd>
          {order ? (
            <>
              #{order.orderNumber ?? order.id.slice(-6)} · {money(order.paidAmount ?? c.totalAmountMinor, order.currency ?? c.currency)}{' '}
              <Badge variant="outline" className="ml-1 h-4 px-1 text-[10px] font-normal">
                {order.status.replace('_', ' ')}
              </Badge>
              {order.refundedAmount ? (
                <span className="text-muted-foreground"> · refunded {money(order.refundedAmount, order.currency)}</span>
              ) : null}
            </>
          ) : (
            '—'
          )}
        </dd>

        <dt className="text-muted-foreground flex items-center gap-1">
          <Truck className="h-3 w-3" /> Tracking
        </dt>
        <dd className="min-w-0 truncate">
          {c.tracking ? (
            <>
              {c.tracking.carrier ? `${c.tracking.carrier} ` : ''}
              {url ? (
                <a href={url} target="_blank" rel="noreferrer" className="font-mono underline-offset-2 hover:underline">
                  {c.tracking.trackingNumber}
                </a>
              ) : (
                <span className="font-mono">{c.tracking.trackingNumber}</span>
              )}
              <span className="text-muted-foreground"> · shipped {day(c.tracking.shippedAt)}</span>
            </>
          ) : (
            'Not shipped yet'
          )}
        </dd>

        <dt className="text-muted-foreground flex items-center gap-1">
          <PackageCheck className="h-3 w-3" /> Renewal
        </dt>
        <dd>
          {c.renewalCancelled
            ? `Stopped ${day(c.renewalCancelled.at)}`
            : c.nextRenewalAt
              ? `Next charge ${day(c.nextRenewalAt)}`
              : 'None scheduled'}
        </dd>
      </dl>

      {actions ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!canRefund} onClick={() => onAction({ kind: 'refund', c })}>
            <RotateCcw className="mr-1 h-3 w-3" /> Refund{refundable ? ` (${money(refundable, order?.currency)})` : ''}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            disabled={!canStopRenewal}
            onClick={() => onAction({ kind: 'renewal', c })}
          >
            <Ban className="mr-1 h-3 w-3" /> Stop renewals
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            disabled={!c.tracking}
            onClick={() => onAction({ kind: 'tracking', c })}
          >
            <Truck className="mr-1 h-3 w-3" /> Resend tracking
          </Button>
        </div>
      ) : null}
    </li>
  );
}

function ActionDialog({
  state,
  actions,
  onClose,
}: {
  state: NonNullable<DialogState>;
  actions: PatientPanelActions;
  onClose: () => void;
}) {
  const [reason, setReason] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const c = state.c;
  const order = c.order;
  const refundable = order ? Math.max(0, (order.paidAmount ?? 0) - (order.refundedAmount ?? 0)) : 0;
  const amountMinor = amount.trim() ? Math.round(Number(amount) * 100) : undefined;
  const amountInvalid = amountMinor !== undefined && (!Number.isFinite(amountMinor) || amountMinor <= 0);

  const needsReason = state.kind !== 'tracking';
  const disabled = busy || (needsReason && reason.trim().length < 3) || amountInvalid;

  const confirm = async () => {
    setBusy(true);
    try {
      if (state.kind === 'refund') await actions.onRefund({ caseId: c.id, amountMinor, reason: reason.trim() });
      if (state.kind === 'renewal') await actions.onCancelRenewal({ caseId: c.id, reason: reason.trim() });
      if (state.kind === 'tracking') await actions.onResendTracking({ caseId: c.id });
      onClose();
    } catch {
      // The container already toasted the error; keep the dialog open to retry.
    } finally {
      setBusy(false);
    }
  };

  const copy = {
    refund: {
      title: 'Refund this order',
      description: `Order #${order?.orderNumber ?? order?.id.slice(-6) ?? ''}. A payment still on hold is released instead of refunded. Leave the amount empty to refund ${money(refundable, order?.currency)}.`,
      cta: 'Refund',
    },
    renewal: {
      title: 'Stop renewals',
      description: 'No more charges for this treatment. What the patient already paid for is not refunded or cancelled.',
      cta: 'Stop renewals',
    },
    tracking: {
      title: 'Send the tracking again',
      description: `The patient gets the "your treatment has shipped" email again with ${c.tracking?.carrier ? `${c.tracking.carrier} ` : ''}${c.tracking?.trackingNumber ?? ''}.`,
      cta: 'Send',
    },
  }[state.kind];

  return (
    <Dialog open onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {state.kind === 'refund' ? (
            <div className="space-y-1.5">
              <Label htmlFor="rx-refund-amount">Amount (optional)</Label>
              <Input
                id="rx-refund-amount"
                inputMode="decimal"
                placeholder={(refundable / 100).toFixed(2)}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
          ) : null}
          {needsReason ? (
            <div className="space-y-1.5">
              <Label htmlFor="rx-action-reason">Reason (kept in the conversation notes)</Label>
              <Textarea id="rx-action-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={state.kind === 'tracking' ? 'default' : 'destructive'}
            onClick={() => void confirm()}
            disabled={disabled}
          >
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            {copy.cta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
